import orderBy from 'lodash/orderBy';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Related from 'components/Related/Related';
import RichText from 'components/RichText/RichText';
import ThreeRowsLayout from 'components/ThreeRowsLayout/ThreeRowsLayout';
import Toast from 'components/Toast/Toast';
import { ContactPageDataCMS } from 'containers/ContactPage/ContactPage.types';
import useClipboard from 'hooks/useClipboard';
import useOverElement from 'hooks/useOverElement';
import { useCopyStore, useGlobalStore } from 'store';
import { appState } from 'utils/appState';
import { OfficeCMS } from 'utils/sharedStrapiQueries/sharedTypes';

import InfoContent from './InfoContent/InfoContent';
import List from './List/List';

import * as S from './DesktopOffices.styles';

export interface DesktopOfficesProps {
  emailTile: ContactPageDataCMS['contactOfficeCards']['emailTile'];
  offices: {
    id: number;
    order: number;
    office: OfficeCMS;
  }[];
  socialsLabel: string;
}

const defaultProps: Partial<DesktopOfficesProps> = {
  emailTile: [],
  offices: [],
};

const DesktopOffices: FunctionComponent<DesktopOfficesProps> = ({
  offices,
  ...rest
}) => {
  const { globalSettings } = useCopyStore(({ copy }) => copy);
  const { isCopied, copyToClipboard } = useClipboard();
  const { locationData } = useGlobalStore();
  const {
    query: { location },
  } = useRouter();

  const globalOrder = useMemo(
    () => offices.filter(o => o.order === 0)[0],
    [offices]
  );

  const cityName =
    (location as string)?.replace(/-and-/g, ' & ')?.replace(/-/g, ' ') ||
    locationData?.name;

  const sortedList = globalOrder
    ? [
        globalOrder.office,
        ...orderBy(
          offices.filter(o => o.order !== 0).map(({ office }) => office),
          ['heading'],
          ['asc']
        ),
      ]
    : orderBy(
        offices.filter(o => o.order !== 0).map(({ office }) => office),
        ['heading'],
        ['asc']
      );

  const actualLocation = useMemo(
    () =>
      sortedList.filter(o =>
        o.heading.toLowerCase().includes(cityName?.toLowerCase())
      )[0] ||
      globalOrder?.office ||
      null,
    [sortedList, locationData, cityName]
  );

  const [activeLocation, setActiveLocation] = useState<OfficeCMS | null>(
    actualLocation
  );
  const [ref] = useOverElement(null, true, false);

  useEffect(() => setActiveLocation(actualLocation), [actualLocation]);

  const list = (
    <List
      list={sortedList}
      activeLocation={activeLocation}
      setActiveLocation={setActiveLocation}
    />
  );

  const mainContent = useCallback(() => {
    if (activeLocation === null)
      return <ThreeRowsLayout leftSideContent={list} rightSideContent={null} />;

    const addressLines = [
      {
        id: 1,
        addressLabel:
          (activeLocation?.findUsLabel || activeLocation?.addressLabel) ?? '',
        contactLabel:
          (activeLocation?.contactUsLabel || activeLocation?.contactLabel) ??
          '',
        addressLine: activeLocation?.addressLine1 ?? '',
        addressButton: activeLocation?.addressButton,
        telephoneNumber: activeLocation?.telephoneNumber ?? '',
        emailFields: activeLocation?.emailFields ?? [],
        emails: activeLocation?.emails ?? [],
        contactText: activeLocation?.contactText ?? '',
      },
      {
        id: 2,
        addressLabel:
          (activeLocation?.findUsLabel || activeLocation?.addressLabel) ?? '',
        contactLabel:
          (activeLocation?.contactUsLabel || activeLocation?.contactLabel) ??
          '',
        addressLine: activeLocation?.addressLine2 ?? '',
        addressButton: activeLocation?.addressButton2,
        telephoneNumber: activeLocation?.telephoneNumber2 ?? '',
        emailFields: activeLocation?.emailFields2 ?? [],
        emails: null,
        contactText: activeLocation?.contactText ?? '',
      },
    ];

    const secondLineVisible =
      addressLines[1].addressLine ||
      addressLines[1].addressButton ||
      addressLines[1].telephoneNumber ||
      addressLines[1].emailFields.length;

    const activeContent = (
      <S.Grid>
        {addressLines.slice(0, secondLineVisible ? 2 : 1).map(props => (
          <InfoContent key={props.id} {...props} />
        ))}
      </S.Grid>
    );

    const contentMedia = (
      <S.ContentWrapper>
        <S.RichTextWrapper richText={activeLocation?.textField} />

        {activeLocation?.introCopy && (
          <S.IntroCopy>
            <RichText
              as="h2"
              size="heading2"
              richText={activeLocation?.introCopy}
              defaultFormatting
            />
          </S.IntroCopy>
        )}

        {activeLocation?.expertiseSectionLabel && (
          <S.ExpertiseSection
            label={activeLocation?.expertiseSectionLabel}
            categories={activeLocation?.expertiseSectionCategories}
          />
        )}

        {activeLocation?.getInTouchSectionLabel && (
          <S.GetInTouchSection
            label={activeLocation?.getInTouchSectionLabel}
            categories={activeLocation?.getInTouchSectionCategories}
            activeLabel={activeLocation?.getInTouchActiveLabel}
            onClickLabel={() => {
              copyToClipboard(activeLocation?.emailFields[0].email);
              appState.analytics.trackEmailCopy('get in touch');
            }}
            onClickContent={e => {
              if (e.target.localName === 'a') {
                appState.analytics.trackLinkedInClick(e.target.href);
              }

              if (
                e.target.localName === 'img' &&
                e.target.parentElement.localName === 'a'
              ) {
                appState.analytics.trackLinkedInClick(
                  e.target.parentElement.href
                );
              }
            }}
          />
        )}

        {activeContent}

        {activeLocation?.relatedArticlesSectionLabel && (
          <Related
            label={activeLocation?.relatedArticlesSectionLabel}
            articles={[
              ...(activeLocation?.relatedArticlesInsights || []),
              ...(activeLocation?.relatedArticlesNews || []),
              ...(activeLocation?.relatedArticlesCaseStudies || []),
            ]}
          />
        )}
      </S.ContentWrapper>
    );

    return (
      <>
        <S.LeftSide>{list}</S.LeftSide>
        <S.RightSide>{contentMedia}</S.RightSide>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocation]);

  return (
    <S.Wrapper ref={ref} {...rest}>
      {mainContent()}
      {isCopied && <Toast text={globalSettings?.copiedText || '✓'} />}
    </S.Wrapper>
  );
};

DesktopOffices.defaultProps = defaultProps;

export default DesktopOffices;
